// src/components/dashboard/LogsBlock.jsx — v8.7.6
//
// Анализ коллеги: "heartbeat-логи серые, важные — яркие"
// Паттерн: шумовые события → text-muted, actionable → нормальный контраст
//
// Логика:
// - source="heartbeat" или message содержит "heartbeat" → dim (text-muted)
// - level=warning/error → нормальный контраст
// - level=info + не heartbeat → средний контраст (text-secondary)

import { useEffect, useState } from "react";
import { workerClient } from "../../api/workerClient";
import SectionTitle from "../ui/SectionTitle";
import Panel from "../ui/Panel";

function parseTs(ts) {
  if (!ts) return null;
  const n = Number(ts);
  return n > 1e12 ? new Date(n) : new Date(n * 1000);
}

function cleanMessage(raw) {
  if (!raw) return "Log entry";
  const pipeIdx = raw.indexOf(" | {");
  if (pipeIdx > -1) return raw.slice(0, pipeIdx).trim();
  const pipeIdx2 = raw.indexOf(" | [");
  if (pipeIdx2 > -1) return raw.slice(0, pipeIdx2).trim();
  return raw;
}

// Является ли запись шумовым heartbeat-логом
function isHeartbeat(log) {
  const msg = (log.message ?? "").toLowerCase();
  const src = (log.source  ?? "").toLowerCase();
  return msg.includes("heartbeat") || src.includes("heartbeat");
}

export default function LogsBlock() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    workerClient("/api/logs")
      .then(res => {
        const list = res?.logs ?? res?.items ?? [];
        setLogs(Array.isArray(list) ? list.slice(0, 10) : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <SectionTitle title="System Logs" />
      <Panel style={{ padding:0, minHeight:80 }}>
        {loading ? (
          <div style={{ padding:"16px 20px", display:"flex", flexDirection:"column", gap:8 }}>
            {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height:34, borderRadius:"var(--radius)" }} />)}
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding:"20px", fontFamily:"var(--font-mono)", fontSize:12, color:"var(--text-muted)" }}>
            No logs recorded
          </div>
        ) : (
          <div>
            {logs.map((log, idx) => {
              const dateObj  = parseTs(log.timestamp);
              const date     = dateObj?.toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) ?? "—";
              const level    = (log.level ?? "info").toLowerCase();
              const message  = cleanMessage(log.message ?? log.source);
              const noise    = isHeartbeat(log);

              // Цвет метки источника
              const tagColor = level === "error" ? "var(--danger)" : level === "warning" ? "var(--warning)" : "var(--text-muted)";

              // Цвет текста сообщения:
              // heartbeat → сильно приглушённый
              // warning/error → нормальный
              // info → средний
              const msgColor = noise
                ? "var(--text-muted)"
                : (level === "error" || level === "warning")
                  ? "var(--text-primary)"
                  : "var(--text-secondary)";

              return (
                <div key={log.id??idx} style={{
                  padding: "10px 20px",
                  borderBottom: idx < logs.length-1 ? "1px solid var(--border-subtle)" : "none",
                  display:"flex", alignItems:"center", justifyContent:"space-between", gap:16,
                  opacity: noise ? 0.55 : 1,   // heartbeat строки тусклее
                }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"var(--font-mono)", fontSize: noise ? 11 : 12, color:msgColor, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:1 }}>
                      {message}
                    </div>
                    <div style={{ fontFamily:"var(--font-mono)", fontSize:9, color:"var(--text-muted)" }}>
                      {date}
                    </div>
                  </div>
                  <span style={{
                    padding:"2px 7px", borderRadius:"var(--radius-sm)",
                    background:"var(--bg-subtle)", border:"1px solid var(--border-subtle)",
                    fontFamily:"var(--font-mono)", fontSize:9,
                    letterSpacing:"0.1em", color: tagColor,
                    flexShrink:0, textTransform:"uppercase",
                  }}>
                    {log.source ?? level}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
